import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import EntityForm from "../../components/ui/EntityForm";
import { toast } from "react-toastify";
import SedesForm from "../../components/modals/SedesFormModal";
import SedesListModal from "../../components/modals/SedesListModal";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const [sedes, setSedes] = useState([]);
  const [showSedesModal, setShowSedesModal] = useState(false);
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [showSedeForm, setShowSedeForm] = useState(false);

  // 🔹 Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/clientes";

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientes(res.data);
      setFilteredClientes(res.data);
    } catch (err) {
      setError("Error al obtener clientes");
      const msg =
        err.response?.data?.message || err.message || "Error desconocido";

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line
  }, []);

  // 🔍 Filtro de búsqueda
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = clientes.filter(
      (c) =>
        c.name.toLowerCase().includes(lowerSearch) ||
        c.nit.toLowerCase().includes(lowerSearch) ||
        c.email.toLowerCase().includes(lowerSearch),
    );
    setFilteredClientes(filtered);
    setCurrentPage(1); // Reinicia a la primera página
  }, [searchTerm, clientes]);

  // 🔹 Cálculo de paginación
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentClientes = filteredClientes.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleEdit = (cliente) => {
    setSelectedCliente(cliente);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedCliente(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedCliente) {
        await axios.put(`${API_URL}/${selectedCliente._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cliente actualizado correctamente ✅");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Cliente creado correctamente ✅");
      }

      setShowModal(false);
      fetchClientes();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al guardar el cliente";
      toast.error(backendMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Cliente eliminado correctamente 🗑️");
      fetchClientes();
    } catch (err) {
      toast.error("Error al eliminar cliente");
    }
  };

  const handleSedes = async (clienteId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/sedes/${clienteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSedes(res.data);
      setSelectedClienteId(clienteId);
      setShowSedesModal(true);
    } catch (err) {
      toast.error("Error al cargar las sedes del cliente");
    }
  };

  const handleDeleteSede = async (sedeId) => {
    if (!window.confirm("¿Eliminar esta sede?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/sedes/${sedeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(sedes.filter((s) => s._id !== sedeId));
      toast.info("Sede eliminada correctamente 🗑️");
    } catch (err) {
      toast.error("Error al eliminar sede");
    }
  };

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Clientes</h2>
          <div className="flex gap-2 items-center w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {hasPermission("clientes.crear") && (
              <Button onClick={handleCreate}>+ Nuevo Cliente</Button>
            )}
          </div>
        </div>
        {error && (
          <Alert variant="destructive">
            <p>{error}</p>
          </Alert>
        )}

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-left">Nit</th>
                <th className="py-2 px-4 text-left">Rol</th>
                <th className="py-2 px-4 text-left">Correo</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentClientes.map((c) => (
                <tr key={c._id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4">{c.name}</td>
                  <td className="py-2 px-4">{c.nit}</td>
                  <td className="py-2 px-4">{c.role}</td>
                  <td className="py-2 px-4">{c.email}</td>
                  <td className="py-2 px-4 text-right">
                    {hasPermission("clientes.editar") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(c)}
                      >
                        Editar
                      </Button>
                    )}
                    {hasPermission("clientes.crear") && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2"
                        onClick={() => handleSedes(c._id)}
                      >
                        Sedes
                      </Button>
                    )}
                    {hasPermission("clientes.eliminar") && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                        onClick={() => handleDelete(c._id)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}

              {currentClientes.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-4 text-gray-500 italic"
                  >
                    No se encontraron clientes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔹 Paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentClientes.length} de {filteredClientes.length}{" "}
              resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {/* Modales existentes */}
        {showModal && (
          <EntityForm
            type="cliente"
            entity={selectedCliente}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}

        {showSedesModal && (
          <SedesListModal
            sedes={sedes}
            onClose={() => setShowSedesModal(false)}
            onDeleteSede={handleDeleteSede}
            onNewSede={() => setShowSedeForm(true)}
          />
        )}

        {showSedeForm && (
          <SedesForm
            clienteId={selectedClienteId}
            onClose={() => setShowSedeForm(false)}
            onSave={async (data) => {
              try {
                const res = await axios.post(
                  `${process.env.REACT_APP_API_URL}/sedes`,
                  data,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                setSedes([...sedes, res.data]);
                setShowSedeForm(false);
                toast.success("Sede creada correctamente ✅");
              } catch (err) {
                toast.error("Error al crear la sede");
              }
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ClientesPage;
